import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ImageOff, ArrowRight } from 'lucide-react';
import { useLang } from '@/lib/LangContext';
import { TID } from '@/constants/testIds';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Explicit curated list for the home page. Ordering here is honored on-screen.
// Match is case-insensitive and ignores punctuation / brand suffixes so
// "BELCANDO", "Nature's Protection" etc. all slot into the right card.
const FEATURED = [
  'Acana',
  'Orijen',
  'Monge',
  'Grandorf',
  'Josera',
  'Belcando',
  'Leonardo',
  'Bewi Dog',
  'Happy Dog',
  'Happy Cat',
  "Nature's Protection",
  'Mera',
];

const normalize = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * Partner brands — curated logo grid.
 *
 * The home page always shows exactly the 12 FEATURED brands below in the
 * configured order. We hit the public `/api/partner-brands` endpoint without
 * the `home_only` filter so the curated list can win regardless of DB flags.
 */
export default function PartnersMarquee() {
  const { t } = useLang();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/partner-brands`);
        const data = await r.json();
        if (!cancelled) setBrands(data.items || []);
      } catch {
        if (!cancelled) setBrands([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Build an index of DB brands by normalized name, then pick in the order
  // specified in FEATURED. Brands not present in DB are rendered as a
  // graceful placeholder tile so the grid stays complete.
  const byName = new Map();
  for (const b of brands) {
    const key = normalize(b.name);
    if (!byName.has(key)) byName.set(key, b);
  }
  const items = FEATURED.map((name) => byName.get(normalize(name)) || { id: `missing-${normalize(name)}`, name, logo_url: null });

  if (loading && brands.length === 0) {
    return (
      <section id="partners" className="py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="h-6 w-56 bg-[#eadfce] rounded animate-pulse mb-6" />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="aspect-square rounded-2xl bg-[#eadfce] animate-pulse" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  const heading = (t.partners && t.partners.heading) || '300+ Brands Trust Us';
  const viewAll = (t.partners && t.partners.viewAll) || 'See all brands';
  // Allow an intentionally blank eyebrow (empty string) to hide the chip.
  const eyebrow = t.partners && t.partners.eyebrow;

  return (
    <section
      id="partners"
      data-testid={TID.partners}
      className="relative py-10 md:py-14"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#F5F2EB]/40 to-transparent pointer-events-none" aria-hidden />
      <div className="relative max-w-7xl mx-auto px-5 md:px-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6 md:mb-8">
          <div>
            {eyebrow && (
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#0A4D8C1A] text-[11px] font-bold uppercase tracking-wider text-[#0A4D8C] mb-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#F25C05]" />
                {eyebrow}
              </div>
            )}
            <h2 className="font-display font-extrabold text-[#05223D] text-2xl md:text-3xl tracking-[-0.02em]">
              {heading}
            </h2>
          </div>
          <Link
            to="/catalogue"
            className="inline-flex items-center gap-1.5 text-[#0A4D8C] font-semibold hover:text-[#F25C05] transition-colors"
            data-testid="partners-see-all"
          >
            {viewAll}
            <ArrowRight size={16} />
          </Link>
        </div>

        <div
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4"
          aria-label="Partner brands"
        >
          {items.map((b) => (
            <div
              key={b.id}
              title={b.name}
              className="group relative aspect-square rounded-2xl bg-white border border-[#eadfce] hover:border-[#0A4D8C]/40 hover:shadow-[0_14px_32px_rgba(10,77,140,0.08)] transition-all duration-300 flex items-center justify-center p-4 overflow-hidden"
              data-testid={`partner-tile-${normalize(b.name)}`}
            >
              {b.logo_url ? (
                <img
                  src={b.logo_url}
                  alt={b.name}
                  loading="lazy"
                  className="max-h-[70%] max-w-[80%] w-auto h-auto object-contain transition-transform duration-300 group-hover:scale-[1.05]"
                />
              ) : (
                <div className="flex flex-col items-center gap-2 text-[#465B70]">
                  <ImageOff className="h-5 w-5" strokeWidth={1.6} />
                  <span className="font-bold text-[#05223D] text-xs text-center leading-tight">{b.name}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
